<template>
    <div class="evaluation-booking-page">
        <v-container class="fill-height d-flex align-center justify-center">
            <v-card flat class="booking-card" max-width="680">

                <!-- Success State (just confirmed booking) -->
                <template v-if="booked">
                    <div class="success-state">
                        <div class="success-icon-wrapper mb-8">
                            <div class="success-circle">
                                <v-icon size="48" color="#FFFFFF">mdi-check</v-icon>
                            </div>
                        </div>

                        <h2 class="success-title mb-3">Evaluation Booked!</h2>
                        <p class="success-subtitle mb-8">
                            Your evaluation session has been scheduled. We'll send you the meeting details via email before your session.
                        </p>

                        <div class="d-flex flex-column align-center">
                            <div class="booking-details mb-6">
                                <div class="detail-row" v-if="bookedDateFormatted">
                                    <v-icon size="20" color="#D4780A">mdi-calendar</v-icon>
                                    <span>{{ bookedDateFormatted }}</span>
                                </div>
                                <div class="detail-row" v-if="bookedTimeFormatted">
                                    <v-icon size="20" color="#D4780A">mdi-clock-outline</v-icon>
                                    <span>{{ bookedTimeFormatted }}</span>
                                </div>
                            </div>

                            <v-btn
                                depressed
                                x-large
                                color="#D4780A"
                                class="start-btn white--text mb-4"
                                @click="goToCourses"
                            >
                                Browse Courses
                            </v-btn>

                            <button class="text-link" @click="goToHome">
                                Go to Dashboard
                            </button>
                        </div>
                    </div>
                </template>

                <!-- Already Booked State (pre-existing evaluation) -->
                <template v-else-if="alreadyBooked">
                    <div class="success-state">
                        <div class="success-icon-wrapper mb-8">
                            <div class="success-circle">
                                <v-icon size="48" color="#FFFFFF">mdi-check</v-icon>
                            </div>
                        </div>

                        <h2 class="success-title mb-3">Session Already Booked</h2>
                        <p class="success-subtitle mb-8">
                            You already have an evaluation session scheduled. We'll send you the meeting details via email before your session.
                        </p>

                        <div class="d-flex flex-column align-center">
                            <div class="booking-details mb-6">
                                <div class="detail-row" v-if="existingEvalDateFormatted">
                                    <v-icon size="20" color="#D4780A">mdi-calendar</v-icon>
                                    <span>{{ existingEvalDateFormatted }}</span>
                                </div>
                                <div class="detail-row" v-if="existingEvalTimeFormatted">
                                    <v-icon size="20" color="#D4780A">mdi-clock-outline</v-icon>
                                    <span>{{ existingEvalTimeFormatted }}</span>
                                </div>
                            </div>

                            <v-btn
                                depressed
                                x-large
                                color="#D4780A"
                                class="start-btn white--text mb-4"
                                @click="goToCourses"
                            >
                                Browse Courses
                            </v-btn>

                            <button class="text-link" @click="goToHome">
                                Go to Dashboard
                            </button>
                        </div>
                    </div>
                </template>

                <!-- Default: Booking State (show calendar) -->
                <template v-else>
                    <div class="card-header">
                        <div class="header-icon">
                            <v-icon size="36" color="#D4780A">mdi-clipboard-check-outline</v-icon>
                        </div>
                        <h1 class="main-title">Book Your Evaluation Session</h1>
                        <p class="main-subtitle">
                            A short 15-minute session with our team to assess your current level and match you with the right course.
                        </p>
                    </div>

                    <div class="card-body">
                        <evaluation-calendar
                            v-model="selectedSlot"
                            :whatsapp-number="whatsappNumber"
                            @slot-selected="onSlotSelected"
                        />

                        <!-- Confirm Button -->
                        <v-btn
                            v-if="selectedSlot"
                            block
                            x-large
                            depressed
                            color="#D4780A"
                            class="confirm-btn white--text mt-6"
                            :loading="booking"
                            @click="confirmBooking"
                        >
                            Confirm Booking
                        </v-btn>
                    </div>
                </template>
            </v-card>
        </v-container>
    </div>
</template>

<script>
import moment from 'moment-timezone';
import EvaluationCalendar from '@/components/custom/EvaluationCalendar.vue';
import GlobalComputed from '@/helpers/global-computed';
import GlobalMethods from '@/helpers/global-methods';

export default {
    name: 'EvaluationBooking',
    components: {
        EvaluationCalendar
    },
    data() {
        return {
            selectedSlot: '',
            slotDetails: null,
            booking: false,
            booked: false,
            bookedData: null,
            // Already-booked guard
            alreadyBooked: false,
            existingEval: null,
            checkingExisting: false,
        };
    },
    computed: {
        whatsappNumber() {
            return this.appData?.whatsapp_number || '';
        },
        bookedDateFormatted() {
            if (!this.bookedData) return '';
            const m = moment.utc(this.bookedData.booked_start).tz(moment.tz.guess());
            return m.format('dddd, MMMM D, YYYY');
        },
        bookedTimeFormatted() {
            if (!this.bookedData) return '';
            const tz = moment.tz.guess();
            const m = moment.utc(this.bookedData.booked_start).tz(tz);
            const end = moment.utc(this.bookedData.booked_end).tz(tz);
            return `${m.format('h:mm A')} – ${end.format('h:mm A')}`;
        },
        existingEvalDateFormatted() {
            if (!this.existingEval) return '';
            const m = moment.utc(this.existingEval.booked_start).tz(moment.tz.guess());
            return m.format('dddd, MMMM D, YYYY');
        },
        existingEvalTimeFormatted() {
            if (!this.existingEval) return '';
            const tz = moment.tz.guess();
            const m = moment.utc(this.existingEval.booked_start).tz(tz);
            const end = moment.utc(this.existingEval.booked_end).tz(tz);
            return `${m.format('h:mm A')} – ${end.format('h:mm A')}`;
        },
        ...GlobalComputed
    },
    methods: {
        onSlotSelected(details) {
            this.slotDetails = details;
        },
        async checkExistingEvaluation() {
            this.checkingExisting = true;
            try {
                const response = await window.axios.get('/evaluations/my');
                if (response.data.has_active_evaluation || response.data.has_completed_evaluation) {
                    // Already booked or already evaluated — redirect to home
                    this.$router.replace({ name: 'home' });
                }
            } catch (e) {
                // silently ignore — if the check fails, just show the calendar
            } finally {
                this.checkingExisting = false;
            }
        },
        async confirmBooking() {
            if (!this.selectedSlot) return;

            this.booking = true;
            try {
                const response = await window.axios.post('/evaluations/book', {
                    booked_start: this.selectedSlot
                });
                this.bookedData = response.data; // Fix: the object is just the response.data itself
                this.booked = true;

                // Refresh user to clear needs_evaluation_redirect
                this.$store.dispatch('refreshUser');
            } catch (error) {
                const msg = error.response?.data?.message || 'Failed to book evaluation. Please try again.';
                this.showMsg(msg, 'error');
            } finally {
                this.booking = false;
            }
        },
        goToCourses() {
            this.$router.push({ name: 'courses' });
        },
        goToHome() {
            this.$router.push({ name: 'home' });
        },
        ...GlobalMethods
    },
    mounted() {
        // Redirect away if evaluation booking is not required at all
        if (this.user && this.user.evaluation_required === false) {
            this.$router.push({ name: 'home' });
            return;
        }
        // Guard: show the 'already booked' state if user already has an active evaluation
        this.checkExistingEvaluation();
    }
};
</script>

<style lang="scss" scoped>
.evaluation-booking-page {
    min-height: 100vh;
    background-color: #FAFAFA;
    padding: 20px 0;

    @media (max-width: 960px) {
        padding: 0;
        background-color: white;
    }
}

.booking-card {
    width: 100%;
    padding: 48px 40px;
    background: white;
    border-radius: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);

    @media (max-width: 960px) {
        padding: 24px 16px 80px;
        border-radius: 0;
        box-shadow: none;
    }
}

.card-header {
    text-align: center;
    margin-bottom: 36px;
}

.header-icon {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    background: #FFF3E0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
}

.main-title {
    font-family: 'Inter', sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #1E1E1E;
    line-height: 1.3;
    margin-bottom: 8px;

    @media (max-width: 600px) {
        font-size: 24px;
    }
}

.main-subtitle {
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    color: #707070;
    line-height: 1.6;
    max-width: 480px;
    margin: 0 auto;
}

.card-body {
    max-width: 520px;
    margin: 0 auto;
}

.confirm-btn {
    height: 52px !important;
    border-radius: 12px !important;
    font-family: 'Montserrat', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: none;
}

/* Success State */
.success-state {
    text-align: center;
    padding: 40px 0;

    @media (max-width: 600px) {
        padding: 20px 0;
    }
}

.success-icon-wrapper {
    display: flex;
    justify-content: center;
}

.success-circle {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #D4780A, #E79007);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 24px rgba(212, 120, 10, 0.3);
}

.success-title {
    font-family: 'Inter', sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #1E1E1E;

    @media (max-width: 600px) {
        font-size: 24px;
    }
}

.success-subtitle {
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    color: #707070;
    line-height: 1.6;
    max-width: 420px;
    margin-left: auto;
    margin-right: auto;
}

.booking-details {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    background: #FFF8F0;
    border: 1px solid #FFE0B2;
    border-radius: 12px;
    padding: 20px 32px;
}

.detail-row {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: #1E1E1E;
}

.start-btn {
    height: 52px !important;
    border-radius: 12px !important;
    font-family: 'Montserrat', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: none;
    padding: 0 40px !important;

    @media (max-width: 600px) {
        width: 100%;
    }
}

.text-link {
    display: block;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #9E9E9E;
    background: none;
    border: none;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
        color: #707070;
    }
}
</style>
